// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultMapPropsOnChangeValue.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { action } from 'mobx';
import { get, omitBy, getOr, isNil, isEqual } from 'lodash/fp';

import MapCollection from 'logic/data/Izenda.MapCollectionConstants';

export default (reportPartDetails, fieldStore) => {
  const reportPartContent = reportPartDetails.reportPartContent || {};

  const getProperties = key => {
    if (!key) {
      const properties = reportPartDetails && reportPartDetails.getReportPartProperties();
      const props = toJS(properties);
      return props;
    }

    return reportPartContent.getReportPartProperties(key);
  };

  return action(
    'OnChangeMapProps',
    (
      schemaData: any,
      changedKey: string,
      changedKeyPath: any,
      changedValue: any,
      changedOthersInfo: any,
      derivedChange: any
    ) => {
      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[changedKey];

      fieldData && (fieldData.value = changedValue);

      const generalInfo = getOr({}, 'generalInfo', schemaData);
      const labels = getOr({}, 'labels', schemaData);
      const view = getOr({}, 'view', schemaData);
      const data = getOr({}, 'data', schemaData);
      const zoomConfig = getValue('zoomConfig', view);

      const legends = getOr({}, 'legends', schemaData);
      const legendSettings = getValue('legendSettings', legends) || {};

      const izBackGroundColor = getValue('map.izBackgroundColor', schemaData) || {};
      const izBorder = getValue('map.izBorder', schemaData) || {};
      const dataRefreshInterval = getValue('dataRefreshInterval', view);
      const colorTheme = getValue('map.colorTheme', schemaData);

      const reportPartProperties = {
        chartType: get('chartType.value', generalInfo),
        continent: get('continent.value', generalInfo),
        country: get('country.value', generalInfo),
        state: get('state.value', generalInfo),
        colorTheme: colorTheme.colorPalette,
        isDefaultTheme: colorTheme.isDefaultTheme,

        commonOptions: {
          /* border */
          'izEntire.borderColor': izBorder['izEntire.borderColor'],
          'izEntire.borderWidth': izBorder['izEntire.borderWidth'],
          'izEntire.borderStyle': izBorder['izEntire.borderStyle'],

          /* background color */
          'izEntire.backgroundColor': izBackGroundColor['izEntire.backgroundColor'],
          'izPlot.backgroundColor': izBackGroundColor['izPlot.backgroundColor'],

          /* label group */
          izLabelOrientation: getValue('izLabelOrientation', labels),
          izHoverLabels: getValue('izHoverLabels', labels),

          /* Legend settings */
          'izLegend.visibility': legendSettings['izLegend.visibility'],
          'izLegend.horizontalAlign': legendSettings['izLegend.horizontalAlign'],
          'izLegend.verticalAlign': legendSettings['izLegend.verticalAlign'],
          'izLegend.borderColor': legendSettings['izLegend.borderColor'],
          'izLegend.borderWidth': legendSettings['izLegend.borderWidth'],
          'izLegend.fontStyling': legendSettings['izLegend.fontStyling'],
          'izLegend.title': legendSettings['izLegend.title'],
          'izLegend.layout': legendSettings['izLegend.layout'],
          'izLegend.backgroundColor': legendSettings['izLegend.backgroundColor'],
          'izLegend.borderVisible': legendSettings['izLegend.borderVisible']
        },

        optionByType: {
          izValueLabel: getValue('izValueLabel', data),
          izShowTooltip: getValue('izShowTooltip', data),
          izMapLabel: getValue('izMapLabel', labels),
          'izMapNavigation.enabled': zoomConfig['izMapNavigation.enabled'],
          'izMapNavigation.align': zoomConfig['izMapNavigation.align'],
          'izMapNavigation.verticalAlign': zoomConfig['izMapNavigation.verticalAlign']
        },

        view: {
          dataRefreshInterval
        }
      };

      changedKey === 'continent' && (reportPartProperties.continentInfo = changedOthersInfo);
      changedKey === 'country' && (reportPartProperties.countryInfo = changedOthersInfo);
      changedKey === 'state' && (reportPartProperties.stateInfo = changedOthersInfo);

      reportPartContent.setReportPartProperties(reportPartProperties);

      if (changedKey === 'chartType') {
        reportPartContent.setChartType(reportPartProperties.chartType);

        reportPartProperties.chartType === 'Continent' && (generalInfo.continent.value = getProperties('continent'));
        reportPartProperties.chartType === 'Country' && (generalInfo.country.value = getProperties('country'));
      }

      if (changedKey === 'country') {
        reportPartContent.updatePointOptionsList();
        const countryInfo = getProperties('countryInfo');
        const stateListName = get('others.STATE_LIST_NAME', countryInfo);

        if (stateListName) {
          generalInfo.state.value = getProperties('state');
          generalInfo.state.props.values.options = MapCollection[stateListName] || [];
        }
      }

      if (changedKey === 'state') {
        reportPartContent.updatePointOptionsList();
      }

      derivedChange && derivedChange(reportPartProperties);
      changedKey !== 'chartType' && reportPartDetails.setReportPartProperties(reportPartProperties);

      return Promise.resolve({ needToUpdate: false, chartProperties: reportPartProperties });
    }
  );
};

/**
 * Get value
 * @param {string} key
 * @param {Object} obj
 */
function getValue(key: string, obj: any) {
  return get([key, 'value'].join('.'), obj);
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartMap/Izenda.DefaultMapPropsOnChangeValue.js